import React from "react";
import PropTypes from "prop-types";
import AutoGrowTextarea from "./AutoGrowTextarea";

// Compact drawer button — the shared secondaryButtonCls is a 36px-tall / 14px
// page-header action button, far too heavy next to a text-xs bullet input. This
// matches the compact inline buttons on /budget-enforcement's drawer (text-xs,
// small padding) so the inspector reads as a dense drawer, not a header bar.
const compactButtonCls =
  "inline-flex items-center whitespace-nowrap rounded-ctrl border border-solid border-btn-line bg-white px-2.5 py-1 text-xs font-medium text-btn-ink hover:bg-btn-fill-hover hover:border-btn-line-hover cursor-pointer transition-colors";

// Compact icon-only remove affordance, so the bullet textarea can span the full
// drawer width. h-[24px] w-[24px] use arbitrary values on purpose — this project
// overrides theme.width/height, so h-6/w-6 would be no-ops.
const removeIconButtonCls =
  "inline-flex items-center justify-center h-[24px] w-[24px] shrink-0 rounded-ctrl text-text-3 hover:text-danger hover:bg-background-grey cursor-pointer transition-colors";

/**
 * BulletsField - the exec-narrative "Key points" bullets list: one plain input per bullet
 * (add/change/remove), no @-token support (a bullet is a short standalone point, not prose that
 * embeds a live figure inline - the same rationale as PlainField). Extracted from NarrativeRail
 * (SP6 Task 5) so both NarrativeRail and BoardReportInspector's exec_narrative field set render
 * bullets identically.
 */
const BulletsField = ({ bullets, disabled, onBulletChange, onAddBullet, onRemoveBullet, onFocus }) => {
  const focus = () => {
    if (onFocus) onFocus("bullets");
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-5">
      <h3 className="text-sm font-semibold text-text mb-3">Key points</h3>
      <ul className="space-y-2">
        {bullets.map((bullet, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className="flex items-start gap-2">
            <AutoGrowTextarea
              ariaLabel={`bullet ${index + 1}`}
              className="flex-1 px-2 py-1.5 border border-border rounded-ctrl text-xs bg-card text-text disabled:text-text-3 resize-none overflow-hidden"
              value={bullet}
              disabled={disabled}
              onChange={(v) => onBulletChange(index, v)}
              onFocus={focus}
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemoveBullet(index)}
                className={removeIconButtonCls}
                aria-label={`remove bullet ${index + 1}`}
              >
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </li>
        ))}
        {bullets.length === 0 && <li className="text-xs text-text-3">No bullets yet.</li>}
      </ul>
      {!disabled && (
        <button type="button" onClick={onAddBullet} className={`mt-3 ${compactButtonCls}`}>
          Add bullet
        </button>
      )}
    </div>
  );
};

BulletsField.propTypes = {
  bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  onBulletChange: PropTypes.func.isRequired,
  onAddBullet: PropTypes.func.isRequired,
  onRemoveBullet: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
};

export default BulletsField;
